'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { toast } from 'sonner'
import {
  CalendarDays, PartyPopper, Moon, Plane, Home, CheckCircle2, XCircle, Percent, CalendarCheck,
} from 'lucide-react'
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { PageShell } from '@/components/ui/page-shell'
import { SectionHeader } from '@/components/ui/section-header'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill } from '@/components/ui/status-pill'
import { TabLoader } from '@/components/ui/loading'
import { MonthGrid, monthLabel } from '@/components/ui/month-grid'
import { todayIST, formatISTDate, isWeekendYMD } from '@/lib/date-utils'
import { formatTime, cn } from '@/lib/utils'

interface DirectoryUser {
  id: string
  name: string
  employeeId: string | null
  department: string | null
  role: string
  isActive?: boolean
}

interface AttendanceRow {
  date: string
  userId?: string | null
  checkIn: string | null
  checkOut: string | null
  status: string
  workMode: string
  user?: { id: string; name: string } | null
}

interface Holiday {
  id: string
  date: string
  name: string
}

type DayKind = 'HOLIDAY' | 'WEEKEND' | 'WORKING' | 'FUTURE'

interface DaySummary {
  date: string
  kind: DayKind
  holidayName?: string
  present: { id: string; name: string; checkIn: string | null; checkOut: string | null; workMode: string }[]
  onLeave: DirectoryUser[]
  onWfh: DirectoryUser[]
  absent: DirectoryUser[]
}

// The roll-call is about employees; ADMIN/HR accounts are excluded so the
// present/absent counts can't be skewed by staff who don't clock in.
const STAFF_ROLES = new Set(['EMPLOYEE', 'INTERN'])

/** Approved requests → { userId: Set<date> }, expanded day by day. */
function expandRanges(rows: any[]): Record<string, Set<string>> {
  const out: Record<string, Set<string>> = {}
  for (const r of rows) {
    if (String(r?.status ?? '').toUpperCase() !== 'APPROVED') continue
    const uid = r.userId ?? r.employee_id
    const start = r.startDate ?? r.from_date
    const end = r.endDate ?? r.to_date
    if (!uid || !start || !end) continue
    if (!out[uid]) out[uid] = new Set()
    for (let d = new Date(start + 'T00:00:00'); d <= new Date(end + 'T00:00:00'); d.setDate(d.getDate() + 1)) {
      out[uid].add(formatISTDate(d))
    }
  }
  return out
}

interface CompanyCalendarProps {
  /** Rendered inside another page's shell — skip the PageShell and header. */
  embedded?: boolean
}

export function CompanyCalendar({ embedded = false }: CompanyCalendarProps = {}) {
  const [month, setMonth] = useState(() => todayIST().slice(0, 7))
  const [loading, setLoading] = useState(true)
  const [staff, setStaff] = useState<DirectoryUser[]>([])
  const [attendance, setAttendance] = useState<AttendanceRow[]>([])
  const [holidays, setHolidays] = useState<Holiday[]>([])
  const [leaveDates, setLeaveDates] = useState<Record<string, Set<string>>>({})
  const [wfhDates, setWfhDates] = useState<Record<string, Set<string>>>({})
  const [openDate, setOpenDate] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [y, m] = month.split('-').map(Number)
      const lastDay = new Date(y, m, 0).getDate()
      const range = `startDate=${month}-01&endDate=${month}-${String(lastDay).padStart(2, '0')}`

      // Fetched independently: a failing side endpoint (leave, WFH, holidays)
      // must not blank the calendar itself.
      const asJson = async (p: Promise<Response>) => (await p).json()
      const [att, users, leaves, wfh, hol] = await Promise.allSettled([
        asJson(apiFetch(`/api/attendance?${range}`)),
        asJson(apiFetch('/api/users')),
        asJson(apiFetch('/api/leave')),
        asJson(apiFetch('/api/wfh')),
        asJson(apiFetch('/api/holidays')),
      ])

      if (att.status === 'fulfilled' && att.value?.attendance) setAttendance(att.value.attendance)
      else if (att.status === 'rejected') toast.error(att.reason?.message || 'Failed to load attendance')

      if (users.status === 'fulfilled' && users.value?.users) {
        setStaff(users.value.users.filter((u: DirectoryUser) => u && u.isActive !== false && STAFF_ROLES.has(u.role)))
      }
      if (leaves.status === 'fulfilled' && leaves.value?.leaves) setLeaveDates(expandRanges(leaves.value.leaves))
      if (wfh.status === 'fulfilled' && wfh.value?.wfhRequests) setWfhDates(expandRanges(wfh.value.wfhRequests))
      if (hol.status === 'fulfilled' && hol.value?.holidays) setHolidays(hol.value.holidays)
    } catch (e: any) {
      console.error('Error loading calendar:', e)
      toast.error(e?.message || 'Failed to load calendar')
    } finally {
      setLoading(false)
    }
  }, [month])

  useEffect(() => { fetchData() }, [fetchData])

  const holidayByDate = useMemo(() => {
    const map: Record<string, Holiday> = {}
    for (const h of holidays) if (h?.date) map[h.date] = h
    return map
  }, [holidays])

  /** One entry per day of the month, with everyone placed in exactly one bucket. */
  const days = useMemo(() => {
    const today = todayIST()
    const [y, m] = month.split('-').map(Number)
    const daysInMonth = new Date(y, m, 0).getDate()

    const attendanceByDate: Record<string, Record<string, AttendanceRow>> = {}
    for (const r of attendance) {
      // Records carry a top-level userId; `user` is a join that can be null when
      // the account was deleted.
      const uid = r.userId ?? r.user?.id
      if (!uid || !r.date) continue
      if (!attendanceByDate[r.date]) attendanceByDate[r.date] = {}
      attendanceByDate[r.date][uid] = r
    }

    const out: Record<string, DaySummary> = {}
    for (let d = 1; d <= daysInMonth; d++) {
      const date = `${month}-${String(d).padStart(2, '0')}`
      const holiday = holidayByDate[date]
      const kind: DayKind = holiday ? 'HOLIDAY'
        : isWeekendYMD(date) ? 'WEEKEND'
        : date > today ? 'FUTURE'
        : 'WORKING'

      const summary: DaySummary = { date, kind, holidayName: holiday?.name, present: [], onLeave: [], onWfh: [], absent: [] }
      const records = attendanceByDate[date] || {}

      for (const u of staff) {
        const rec = records[u.id]
        if (rec?.checkIn) {
          summary.present.push({ id: u.id, name: u.name, checkIn: rec.checkIn, checkOut: rec.checkOut, workMode: rec.workMode })
        } else if (leaveDates[u.id]?.has(date)) {
          summary.onLeave.push(u)
        } else if (wfhDates[u.id]?.has(date)) {
          summary.onWfh.push(u)
        } else if (kind === 'WORKING') {
          // Only a past working day can produce an absence.
          summary.absent.push(u)
        }
      }
      out[date] = summary
    }
    return out
  }, [month, attendance, staff, leaveDates, wfhDates, holidayByDate])

  const stats = useMemo(() => {
    let working = 0, presentTotal = 0, absentTotal = 0, leaveTotal = 0
    let holidayCount = 0
    for (const s of Object.values(days)) {
      if (s.kind === 'HOLIDAY') { holidayCount++; continue }
      if (s.kind !== 'WORKING') continue
      working++
      presentTotal += s.present.length
      absentTotal += s.absent.length
      leaveTotal += s.onLeave.length
    }
    const expected = working * staff.length
    const rate = expected > 0 ? Math.round((presentTotal / expected) * 100) : 0
    return { working, presentTotal, absentTotal, leaveTotal, holidayCount, rate }
  }, [days, staff.length])

  const selected = openDate ? days[openDate] : null

  const Shell = embedded
    ? ({ children }: { children: React.ReactNode }) => <div className="space-y-5 md:space-y-6">{children}</div>
    : PageShell

  return (
    <Shell>
      {!embedded && (
        <SectionHeader
          icon={CalendarDays}
          title="Attendance calendar"
          description="Holidays and day-by-day attendance across the team"
        />
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiTile label="Working days" value={stats.working} icon={CalendarCheck} tone="blue" hint={monthLabel(month)} />
        <KpiTile label="Holidays" value={stats.holidayCount} icon={PartyPopper} tone="cyan" hint="Declared this month" />
        <KpiTile label="Attendance" value={`${stats.rate}%`} icon={Percent} tone="green" hint={`${staff.length} employees`} />
        <KpiTile label="Absences" value={stats.absentTotal} icon={XCircle} tone="red" hint="Employee-days, past days only" />
        <KpiTile label="Leave days" value={stats.leaveTotal} icon={Plane} tone="amber" hint="Approved, employee-days" />
      </div>

      <GlassCard>
        {loading ? (
          <TabLoader />
        ) : (
          <MonthGrid
            month={month}
            onMonthChange={setMonth}
            onDayClick={(d) => setOpenDate(d)}
            hint="Click a day to see who was present, on leave or absent."
            toolbar={
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-neutral-500 dark:text-white/50">
                {[
                  ['bg-emerald-500', 'Present'],
                  ['bg-rose-500', 'Absent'],
                  ['bg-cyan-500', 'Holiday'],
                  ['bg-neutral-300 dark:bg-white/20', 'Weekend'],
                ].map(([dot, label]) => (
                  <span key={label} className="inline-flex items-center gap-1.5">
                    <span className={cn('h-1.5 w-1.5 rounded-full', dot)} />
                    {label}
                  </span>
                ))}
              </div>
            }
            renderDay={(date) => {
              const s = days[date]
              if (!s) return { className: 'bg-transparent ring-neutral-200/60 dark:ring-white/5', disabled: true }

              if (s.kind === 'HOLIDAY') {
                return {
                  title: `${date} · ${s.holidayName}`,
                  className: 'bg-cyan-50 ring-cyan-200 hover:ring-cyan-300 dark:bg-cyan-500/10 dark:ring-cyan-500/25 dark:hover:ring-cyan-500/40',
                  content: (
                    <>
                      <PartyPopper className="h-4 w-4 text-cyan-600 dark:text-cyan-300 sm:h-[18px] sm:w-[18px]" />
                      <span className="max-w-full truncate text-[9px] leading-none text-cyan-700 dark:text-cyan-300 sm:text-[10px]">
                        {s.holidayName}
                      </span>
                    </>
                  ),
                }
              }

              if (s.kind === 'WEEKEND') {
                return {
                  title: `${date} · Weekend`,
                  className: 'bg-neutral-50 ring-neutral-200/70 dark:bg-white/[0.02] dark:ring-white/5',
                  content: <Moon className="h-4 w-4 text-neutral-300 dark:text-white/20 sm:h-[18px] sm:w-[18px]" />,
                  disabled: true,
                }
              }

              if (s.kind === 'FUTURE') {
                return {
                  title: date,
                  className: 'bg-transparent ring-neutral-200/60 dark:ring-white/5',
                  disabled: true,
                }
              }

              // Past working day: tint by how much of the team showed up.
              const tone = s.present.length === 0
                ? 'bg-rose-50 ring-rose-200 hover:ring-rose-300 dark:bg-rose-500/10 dark:ring-rose-500/25 dark:hover:ring-rose-500/40'
                : s.absent.length === 0
                  ? 'bg-emerald-50 ring-emerald-200 hover:ring-emerald-300 dark:bg-emerald-500/10 dark:ring-emerald-500/25 dark:hover:ring-emerald-500/40'
                  : 'bg-amber-50 ring-amber-200 hover:ring-amber-300 dark:bg-amber-500/10 dark:ring-amber-500/25 dark:hover:ring-amber-500/40'
              return {
                title: `${date} · ${s.present.length} present · ${s.absent.length} absent · ${s.onLeave.length} on leave`,
                className: tone,
                content: (
                  <>
                    <span className="text-sm font-semibold leading-none tabular-nums text-emerald-700 dark:text-emerald-300">
                      {s.present.length}
                    </span>
                    <span className="text-[9px] leading-none tabular-nums text-rose-600 dark:text-rose-300 sm:text-[10px]">
                      {s.absent.length} out
                    </span>
                  </>
                ),
              }
            }}
          />
        )}
      </GlassCard>

      <Dialog open={!!openDate} onOpenChange={(open) => !open && setOpenDate(null)}>
        <DialogContent className="bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-white/10 max-w-lg w-full text-neutral-900 dark:text-neutral-100">
          <DialogHeader>
            <DialogTitle className="text-neutral-900 dark:text-white/90">
              {selected ? new Date(selected.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }) : ''}
            </DialogTitle>
            <DialogDescription className="text-neutral-500 dark:text-white/50">
              {selected?.kind === 'HOLIDAY'
                ? `Holiday — ${selected.holidayName}`
                : selected
                  ? `${selected.present.length} present · ${selected.onWfh.length} WFH · ${selected.onLeave.length} on leave · ${selected.absent.length} absent`
                  : ''}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[55vh] space-y-4 overflow-y-auto pr-1">
            <DayGroup icon={CheckCircle2} tone="green" title="Present" count={selected?.present.length ?? 0}>
              {selected?.present.map(p => (
                <li key={p.id} className="flex items-center justify-between gap-2 text-sm">
                  <span className="truncate text-neutral-800 dark:text-white/80">{p.name}</span>
                  <span className="shrink-0 tabular-nums text-xs text-neutral-500 dark:text-white/50">
                    {formatTime(p.checkIn)}{p.checkOut ? ` → ${formatTime(p.checkOut)}` : ''}
                  </span>
                </li>
              ))}
            </DayGroup>

            <DayGroup icon={Home} tone="violet" title="Work from home" count={selected?.onWfh.length ?? 0}>
              {selected?.onWfh.map(u => <SimpleRow key={u.id} name={u.name} sub={u.department || u.employeeId} />)}
            </DayGroup>

            <DayGroup icon={Plane} tone="amber" title="On leave" count={selected?.onLeave.length ?? 0}>
              {selected?.onLeave.map(u => <SimpleRow key={u.id} name={u.name} sub={u.department || u.employeeId} />)}
            </DayGroup>

            <DayGroup icon={XCircle} tone="red" title="Absent" count={selected?.absent.length ?? 0}>
              {selected?.absent.map(u => <SimpleRow key={u.id} name={u.name} sub={u.department || u.employeeId} />)}
            </DayGroup>
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  )
}

function SimpleRow({ name, sub }: { name: string; sub?: string | null }) {
  return (
    <li className="flex items-center justify-between gap-2 text-sm">
      <span className="truncate text-neutral-800 dark:text-white/80">{name}</span>
      {sub && <span className="shrink-0 text-xs text-neutral-400 dark:text-white/40">{sub}</span>}
    </li>
  )
}

function DayGroup({
  icon: Icon, tone, title, count, children,
}: {
  icon: typeof CheckCircle2
  tone: 'green' | 'red' | 'amber' | 'violet'
  title: string
  count: number
  children: React.ReactNode
}) {
  if (!count) return null
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <Icon className="h-4 w-4 text-neutral-400 dark:text-white/40" />
        <h4 className="text-sm font-medium text-neutral-900 dark:text-white/90">{title}</h4>
        <StatusPill tone={tone}>{count}</StatusPill>
      </div>
      <ul className="space-y-1 pl-6">{children}</ul>
    </div>
  )
}
